import { CardLastMessageTimedelta, CardTitle } from "../common";
import React, { useEffect, useImperativeHandle, useState } from "react";

import PropTypes from "prop-types";
import { convertUTCToLocal } from "../../helpers/utils";

// Helper component for cycling through messages with animations
const RotatingMessages = ({ messages, sizeKey, cardH }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [clamp, setClamp] = useState("line-clamp-3");

  useEffect(() => {
    // Set the line clamp based on the card height
    setClamp(`line-clamp-${3 * cardH}`);

    if (messages.length > 1) {
      const interval = setInterval(() => {
        setFade(false);
        setTimeout(() => {
          setCurrentMessageIndex(
            (prevIndex) => (prevIndex + 1) % messages.length
          );
          setFade(true);
        }, 500); // Duration should match the CSS transition
      }, 3000); // Change every 3 seconds

      return () => clearInterval(interval);
    }
  }, [messages.length, cardH]);

  if (messages.length === 0) {
    return (
      <div className="relative h-full flex items-center justify-center">
        <p className={`text-gray-600 text-sm`}>No messages</p>
      </div>
    );
  }

  return (
    <div
      className={`relative h-full ${sizeKey === "1x1" ? "text-center" : ""}`}
    >
      {messages.map((message, index) => (
        <div
          key={message.id || index}
          className={`absolute inset-0 transition-opacity duration-500 ${
            index === currentMessageIndex
              ? fade
                ? "opacity-100"
                : "opacity-0"
              : "opacity-0"
          }`}
        >
          <p
            className={`text-gray-600 text-sm overflow-hidden text-ellipsis break-words ${clamp}`}
          >
            {message.content}
          </p>
          <p className={`text-gray-500 text-xs text-sm`}>
            {convertUTCToLocal(message.timestamp)}
          </p>
        </div>
      ))}
    </div>
  );
};

RotatingMessages.propTypes = {
  messages: PropTypes.array.isRequired,
  sizeKey: PropTypes.string.isRequired,
};

const TopicCard = React.forwardRef(
  ({ card, topic, messages, w, h, displayVariant, displayParameters }, ref) => {
    // Use safeMessages to ensure we always have an array.
    const safeMessages = messages || [];
    const lastMessage = safeMessages.length > 0 ? safeMessages[0] : null;

    // Define the action for this card.
    const action = (setSelectedCard) => {
      setSelectedCard(card);
    };

    useImperativeHandle(ref, () => ({
      action,
    }));

    // Use the first 3 messages for display (if available).
    const messagesToDisplay = safeMessages.slice(0, 3);
    const sizeKey = `${w}x${h}`;

    // Fallback time text if no messages.
    let timeText = "No messages";
    if (lastMessage && lastMessage.timestamp) {
      const messageTime = convertUTCToLocal(lastMessage.timestamp);
      // For simplicity, we'll just show the converted time.
      timeText = messageTime.toLocaleString();
    }

    // Determine rendering based on the card variant and size.
    const renderContent = () => {
      switch (sizeKey) {
        case "1x1":
          return (
            <div className="flex items-center justify-center w-full h-full">
              <CardTitle title={topic.name} />
            </div>
          );
        case "1x2":
        case "2x1":
          return (
            <div className="flex flex-col items-center justify-center w-full h-full">
              <CardTitle title={topic.name} />
              <p
                className="text-gray-600 text-sm truncate"
                title={messagesToDisplay[0]?.content || "No messages"}
              >
                {messagesToDisplay[0]?.content || "No messages"}
              </p>
            </div>
          );
        case "1x3":
        case "3x1":
          return (
            <div className="flex flex-col items-center justify-center w-full h-full">
              <CardTitle title={topic.name} />
              <p
                className="text-gray-600 text-sm truncate"
                title={messagesToDisplay[0]?.content || "No messages"}
              >
                {messagesToDisplay[0]?.content || "No messages"}
              </p>
              <p
                className="text-gray-500 text-xs truncate"
                title={convertUTCToLocal(messagesToDisplay[0]?.timestamp)}
              >
                {convertUTCToLocal(messagesToDisplay[0]?.timestamp)}
              </p>
            </div>
          );
        case "2x2":
        case "2x3":
        case "3x2":
        case "3x3":
          return (
            <div className="flex flex-col justify-between w-full h-full">
              <CardTitle title={topic.name} />
              <RotatingMessages
                messages={messagesToDisplay}
                sizeKey={sizeKey}
                cardH={h}
              />
              {messagesToDisplay.length > 1 && (
                <CardLastMessageTimedelta lastMessage={messagesToDisplay[0]} />
              )}
            </div>
          );
        default:
          return (
            <div className="flex flex-col justify-between w-full h-full">
              <CardTitle title={topic.name} />

              <RotatingMessages
                messages={messagesToDisplay}
                sizeKey={sizeKey}
                cardH={h}
              />

              {messagesToDisplay.length > 1 && (
                <CardLastMessageTimedelta lastMessage={messagesToDisplay[0]} />
              )}
            </div>
          );
      }
    };

    const renderFunction = {
      default: renderContent,
      paragraph: () => (
        <div className="flex flex-col w-full h-full p-1">
          {displayParameters?.showTitle && <CardTitle title={topic.name} />}
          <p
            className="text-gray-600 text-md"
            title={messagesToDisplay[0]?.content || "No messages"}
          >
            {safeMessages.length > 0
              ? safeMessages[safeMessages.length - 1].content
              : "No messages"}
          </p>
        </div>
      ),
      codeblock: () => (
        <div className="flex flex-col w-full h-full p-1">
          <CardTitle title={topic.name} />
          <pre
            className="text-gray-600 text-sm truncate"
            title={messagesToDisplay[0]?.content || "No messages"}
          >
            {safeMessages.length > 0
              ? safeMessages[safeMessages.length - 1].content
              : "No messages"}
          </pre>
        </div>
      ),
    };

    const renderVariant =
      renderFunction[displayVariant] || renderFunction.default;

    return (
      <div
        className="p-1 shadow-md cursor-pointer transition-transform duration-300 w-full h-full flex flex-col bg-gray-50"
        style={{ display: "flex", flexDirection: "column" }}
      >
        {renderVariant()}
      </div>
    );
  }
);

TopicCard.variants = ["default", "paragraph", "codeblock"];
TopicCard.variantParameters = {
  default: {},
  paragraph: {
    showTitle: {
      label: "Show Topic Title",
      type: "bool",
      default: true,
    },
  },
  codeblock: {},
};
TopicCard.propTypes = {
  topic: PropTypes.object.isRequired,
  w: PropTypes.number.isRequired,
  h: PropTypes.number.isRequired,
};

export default TopicCard;
